import { Checkbox, FaqComponent, Icon } from '@perch-labs/shrike';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/button';
import { FaqButton } from '~/components/button/button.style';
import { Wrapper } from '~/components/customer-details/customer-details.style';
import { COMMUNITY_SOLAR } from '~/components/dialogs';
import { ErrorMessage } from '~/components/error-message';
import {
  ImageSelector,
  type ImageType,
  UTILITY_CONNECT_IMAGE,
  ORLEANS_SOLAR,
} from '~/components/image';
import { TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { Description, Title, Text, Ul, UlListItem } from '~/components/typography';
import { useCopy } from '~/hooks';
import { LDClient, LDFlags } from '~/providers';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { UploadedImage } from '~/utils/files';
import { EventName, Logger, Status } from '~/utils/logger';
import { store } from '~/utils/store';
import { getFaqData } from '../faq.data';
import { LMIProofDocSkipConfirmationDialog } from '../lmi-proof-doc-skip-confirmation-dialog';
import { DocumentDetails, Document } from './document-details.component';
import { LMIProofDocUploader } from './lmi-proof-doc-uploader.component';

export const LMIProofDocsUpload = ({
  context,
  next,
  skip,
  overrideCopy,
  flags,
  error,
  loading,
  pending,
}: LMIProofDocsUploadProps) => {
  const enableManualProofCapture = LDClient.variation(LDFlags.enableManualProofCapture);
  const copy = useCopy(context, checkCopy, overrideCopy);
  const [failure, setFailure] = useState(false);
  const [docs, setDocs] = useState<LMIProofDoc[] | Document[]>([]);
  const [documentTypes, setDocumentTypes] = useState([] as string[]);
  const [showSkipConfirmationDialog, setShowSkipConfirmationDialog] = useState(false);
  const [documentTermsChecked, setDocumentTermsChecked] = useState(!enableManualProofCapture);
  const [validationPending, setValidationPending] = useState(false);
  const hasErrorDoc = docs.some((doc: LMIProofDoc | Document) => 'image' in doc && doc.image.error);

  const [showSkipThisStepLink, setShowSkipThisStepLink] = useState(
    LDClient.variation(LDFlags.allowUserToSkipLmiProof),
  );

  const [shouldBeDisabled, setShouldBeDisabled] = useState(false);

  const faqRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    /**
     * if user reloads the page while in upload page, the component mounts
     * before the solar machine initialize and identify() with Launch Darkly.
     * to prevent the component from using stale data, we add a listener to
     * update the needed LD values 'onChange' below.
     */
    LDClient.on('change', () => {
      setShowSkipThisStepLink(LDClient.variation(LDFlags.allowUserToSkipLmiProof));
      if (context.shouldEnableSkip !== undefined) {
        setShowSkipThisStepLink(context.shouldEnableSkip); // For cases where we reload the page
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShowSkipThisStepLink(LDClient.variation(LDFlags.allowUserToSkipLmiProof));
    if (context.shouldEnableSkip !== undefined) {
      setShowSkipThisStepLink(context.shouldEnableSkip);
    }
  }, [context.shouldEnableSkip]);

  const documentTypesForSelect = context.labelOptions ?? [];
  const actionPending = pending || loading || validationPending;

  const disableNextButton = () => {
    return enableManualProofCapture && !flags?.showFaqButton
      ? actionPending || failure || !documentTermsChecked || !shouldBeDisabled
      : actionPending || failure || docs.length === 0 || hasErrorDoc;
  };

  return (
    <Wrapper ref={backToTopRef} style={{ border: 'none' }}>
      <TwoColumnGrid testId="screen-LMIProofDocsUpload">
        <TwoColumnGrid.Primary>
          {!!flags && (
            <Text color="secondary" fontWeight="700" fontSize="16px">
              {copy.step}
            </Text>
          )}
          <Title mb={4}>{copy.title}</Title>
          <Description mb={6}>{copy.description}</Description>
          {!!enableManualProofCapture &&
            !flags?.showFaqButton &&
            !context?.hasIra &&
            !!copy.info && (
              <>
                <Description mb={4}>{copy.info}</Description>
                <Ul mb={4}>
                  {copy.infoList.map(text => (
                    <UlListItem key={text}>{text}</UlListItem>
                  ))}
                </Ul>
                <Description mb={4}>{copy.missingDocumentInfo}</Description>
                <Description mb={4}>{copy.additionalDocumentInfo}</Description>
              </>
            )}
          {flags?.showFaqButton ? (
            <FaqButton
              fullWidth={false}
              mode="default"
              onClick={() => {
                faqRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Icon
                color="primary"
                icon="InfoCircleInverted"
                margin="5px"
                opacity="high"
                path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                scale={2}
                title="InfoCircleInverted"
              />
              View FAQs
            </FaqButton>
          ) : null}
          {!enableManualProofCapture || flags?.showFaqButton ? (
            <LMIProofDocUploader
              documentTypes={documentTypes}
              docs={docs as LMIProofDoc[]}
              pending={actionPending}
              allDocumentTypes={documentTypesForSelect}
              onDocsChange={setDocs}
              onDocumentTypesChange={setDocumentTypes}
              onFailureChange={setFailure}
            />
          ) : (
            <DocumentDetails
              allDocumentTypes={documentTypesForSelect}
              pending={actionPending}
              setShouldBeDisabled={setShouldBeDisabled}
              setDocs={setDocs}
              context={context}
              onValidationPendingChange={setValidationPending}
            />
          )}
          {!!enableManualProofCapture && !flags?.showFaqButton && (
            <Checkbox
              margin="16px 0 16px 0"
              checked={documentTermsChecked}
              disabled={actionPending}
              onChange={e => setDocumentTermsChecked(e.target.checked)}
            >
              <Text textStyle="paragraph400" color="secondary">
                {copy.documentTerms}
              </Text>
            </Checkbox>
          )}
          {error ? <ErrorMessage error={error} /> : null}
          <Button
            type="submit"
            mode="primary"
            disabled={disableNextButton()}
            loading={loading || validationPending}
            mt={6}
            mb={validationPending ? 0 : 6}
            onClick={() => {
              if (actionPending) return;
              next(docs);
            }}
            testId="lmi-proof-docs-upload-next"
          >
            {copy.saveContinue}
          </Button>
          {validationPending ? (
            <Text fontSize="14px" mt={2} mb={6} opacity="medium">
              {copy.validationPendingMessage}
            </Text>
          ) : null}
          {!!showSkipThisStepLink && (
            <Link
              color="primary"
              type="standalone"
              alignSelf="center"
              onClick={e => {
                e.stopPropagation();
                if (actionPending) return;
                if (docs.length) {
                  setShowSkipConfirmationDialog(true);
                }
                store.set('PROOF_DOC_SKIPPED', true);
                updateLogsWhenSkipped();
                skip([]);
              }}
              openInNewTab={false}
              testId="lmi-proof-docs-upload-skip"
            >
              {copy.skip}
            </Link>
          )}
        </TwoColumnGrid.Primary>
        <TwoColumnGrid.Secondary>
          {flags?.showFaqButton ? (
            <Wrapper
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent
                /** shrike need to properly export FaqData type */
                data={getFaqData(documentTypesForSelect) as any}
                header="Frequently Asked Questions"
              />
            </Wrapper>
          ) : (
            <TwoColumnGrid.Secondary>
              <ImageSelector type={copy.imageType} />
            </TwoColumnGrid.Secondary>
          )}
        </TwoColumnGrid.Secondary>
        {flags?.showFaqButton ? (
          <TwoColumnGrid.MobileOnlySecondary>
            <Wrapper
              ref={faqRef}
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent
                /** shrike need to properly export FaqData type */
                data={getFaqData(documentTypesForSelect) as any}
                header="Frequently Asked Questions"
              />
            </Wrapper>
            <Wrapper
              onClick={() => {
                backToTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: ' 2px',
              }}
            >
              <Text textAlign="center" mt={5} paddingBottom={5} textStyle="heading600">
                Back to top
                <Icon
                  color="primary"
                  icon="ArrowUp"
                  margin="5px"
                  opacity="high"
                  path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                  scale={2}
                  title="ArrowUp"
                />
              </Text>
            </Wrapper>
          </TwoColumnGrid.MobileOnlySecondary>
        ) : null}
      </TwoColumnGrid>
      {!!showSkipConfirmationDialog && (
        <LMIProofDocSkipConfirmationDialog
          onSkip={() => {
            updateLogsWhenSkipped();
            setShowSkipConfirmationDialog(false);
            setDocs([]);
            skip([]);
          }}
          onCancel={() => setShowSkipConfirmationDialog(false)}
        />
      )}
    </Wrapper>
  );
};

const updateLogsWhenSkipped = () => {
  Logger.logEvent(
    {
      status: Status.Completed,
      skippedLmiProofDocs: true,
    },
    EventName.ProofDocUploadScreen,
  );
};

/*
This copy is used accross all the funnels
*/

export const checkCopy = context => {
  if (context?.hasIra) {
    return lmiCopy();
  }
  return defaultCopy();
};

export const lmiCopy = () => {
  return {
    title: 'Help us verify your eligibility for income-based benefits',
    description: (
      <>
        If you participate in an assistance program, you may qualify for{' '}
        <strong>faster enrollment and additional solar savings</strong>. Upload a document like a
        card, letter, utility bill, or program screenshot that proves your participation in one of
        these programs.
      </>
    ),
    infoList: [],
    missingDocumentInfo: '',
    imageType: ORLEANS_SOLAR as ImageType,
    next: 'Save and Continue',
    skip: 'Skip this step',
    documentTerms:
      'By checking this box, I hereby affirm that the documentation I have provided is true and accurate to the best of my knowledge, is currently effective and/or has been effective within the past twelve (12) months, and corresponds with my own enrollment and participation, or the enrollment and participation of another member of my household, in the income-qualifying assistance program identified therein.',
    nonLmiNext: 'Continue',
    descriptionLineTwo: '',
    descriptionLineOne: '',
    lmiSectionTitle: '',
    lmiSectionDescription: '',
    saveContinue: 'Save and continue',
    additionalDocumentInfo: '',
    step: '',
    info: '',
    validationPendingMessage: 'Validating document, this may take up to a minute...',
  };
};

export const defaultCopy = () => ({
  description: (
    <>
      If you participate in an assistance program, you may qualify for{' '}
      <strong>faster enrollment and additional solar savings</strong>. Upload a document like a
      card, letter, utility bill, or program screenshot that proves your participation in one of
      these programs.
    </>
  ),
  info: '',
  missingDocumentInfo: '',
  infoList: [],
  additionalDocumentInfo: '',
  documentTerms:
    'By checking this box, I hereby affirm that the documentation I have provided is true and accurate to the best of my knowledge, is currently effective and/or has been effective within the past twelve (12) months, and corresponds with my own enrollment and participation, or the enrollment and participation of another member of my household, in the income-qualifying assistance program identified therein.',
  learnMoreModal: COMMUNITY_SOLAR,
  imageType: UTILITY_CONNECT_IMAGE as ImageType,
  saveContinue: 'Save and continue',
  skip: 'Skip this step',
  title: 'Help us verify your eligibility for income-based benefits',
  step: 'Step 3 of 4',
  validationPendingMessage: 'Validating document, this may take up to a minute...',
});

export type LmiProofDocsUploadCopy = ReturnType<typeof defaultCopy>;

export interface LMIProofDocsUploadProps {
  context: LMIProofDocsUploadContext;
  flags?: {
    showFaqButton: boolean;
  };
  next: (docs) => void;
  skip: (docs) => void;
  overrideCopy?: (context: LMIProofDocsUploadContext) => Partial<LmiProofDocsUploadCopy>;
  error?: Error | null;
  loading: boolean;
  pending: boolean;
}

export interface LMIProofDoc {
  documentType: string;
  image: UploadedImage;
}

export interface LMIProofDocsUploadContext {
  funnelPacket?: FetchFunnelPacketResponse;
  funnelConfiguration?: {
    projectDetails?: {
      ira?: boolean | null;
      lmiCapacityAvailable?: boolean | null;
      lmiProductId?: string | null;
    } | null;
    supportedCustomerTypes?: Array<string | null> | null;
  } | null;
  labelOptions?: string[] | Record<string, string> | null;
  docs: LMIProofDoc[] | Document[];
  documentTypes: string[];
  shouldEnableSkip?: boolean;
  hasIra?: boolean;
  funnelId?: string;
  customerType?: string;
  resolvedPerchProductId?: string;
}

export interface UploadContext {
  funnelPacket: FetchFunnelPacketResponse;
  documentTypes: string[];
  docs: LMIProofDoc[] | Document[];
  shouldEnableSkip?: boolean;
  promoCode?: string;
  utmParams?: Record<string, string | number>;
}
